import React from 'react';
import { Loader, Button } from '@ublend-npm/aulaui-next';
import { Assignment } from './assignmentType';
import { useFetchData } from './useFetchData';
import { useAulaEditorContext } from '../../EditorContext';
import { ListItem } from './ListItem';
import {
  AssignmentsListWrapper,
  AssignmentsListWrapperWithPadding,
  EmptyAssignmentTextWrapper,
  Text,
  ButtonLinkWrapper,
  WarningIcon,
  FlexContainer,
} from './AssignmentList.styled';
import { StatusTypes } from '../../lib/loadingStatusTypes';

const sortAssignments = (a: Assignment, b: Assignment) => {
  const textA = a.title.toUpperCase();
  const textB = b.title.toUpperCase();
  return textA < textB ? -1 : textA > textB ? 1 : 0;
};

export type AssignmentsListProps = {
  /**
   * fetching assignments endpoint
   * /assignments
   */
  assignmentsEndpoint: string;

  /**
   * function that is called when an assignment is selected
   */
  onSelect: (data: Assignment) => void;
};

export const AssignmentsList = ({
  assignmentsEndpoint,
  onSelect,
}: AssignmentsListProps) => {
  const { config } = useAulaEditorContext();
  const { spaceId } = config;
  const assignmentsUrl = `${assignmentsEndpoint}?spaceId=${spaceId}&excludeExtensions=true&page=0&displayLimit=1000`;

  const { state, retry } = useFetchData(assignmentsUrl);

  const {
    status,
    data: { assignments },
  } = state;
  if (status === StatusTypes.IDLE || status === StatusTypes.LOADING) {
    return (
      <AssignmentsListWrapperWithPadding data-testid="loader">
        <Loader thickness={6} />
      </AssignmentsListWrapperWithPadding>
    );
  }

  if (status === StatusTypes.FAILED) {
    return (
      <AssignmentsListWrapperWithPadding role="menuitem">
        <FlexContainer>
          <WarningIcon />
          <Text>Can&apos;t load assignments.</Text>
          <ButtonLinkWrapper>
            <Button type="text" onClick={retry}>
              Retry
            </Button>
          </ButtonLinkWrapper>
        </FlexContainer>
      </AssignmentsListWrapperWithPadding>
    );
  }

  if (!assignments?.length) {
    const createAssignmentUrl = `/#/dashboard/${spaceId}/journey/assignments`;

    return (
      <AssignmentsListWrapperWithPadding role="menuitem">
        <EmptyAssignmentTextWrapper>
          <Text>No assignments in this space.</Text>
        </EmptyAssignmentTextWrapper>
        <div>
          <Button type="primary" href={createAssignmentUrl}>
            Create new
          </Button>
        </div>
      </AssignmentsListWrapperWithPadding>
    );
  }

  const sortedAssignments = assignments.sort(sortAssignments);
  return (
    <AssignmentsListWrapper role="menuitem">
      {sortedAssignments.map((assignment: Assignment, i: number) => (
        <ListItem
          assignment={assignment}
          onSelect={onSelect}
          key={assignment.id}
          first={i === 0}
          dataTestId={`item-${assignment.id}`}
        />
      ))}
    </AssignmentsListWrapper>
  );
};
